import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { SVGProps } from "react";
import { INSPECTION_KINDS, INSPECTION_STATUSES, inspectionKindLabel } from "@rentorng/core";
import { api } from "@/lib/api";
import { ExportButton } from "@/components/export-button";
import { Pagination, SearchBox } from "@/components/list-toolbar";
import { useListParams, type PageMeta } from "@/lib/list-params";
import { titleCase, useFormat } from "@/lib/format";
import { useApiMutation } from "@/lib/mutations";
import { resolveProperty, resolveStaff, resolveUnit, searchProperties, searchStaff, searchUnits } from "@/lib/pickers";
import { SearchSelect } from "@/components/search-select";
import { useSession } from "@/lib/session";
import { IconClipboardCheck } from "@/components/icons";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardTitle,
  EmptyState,
  ErrorState,
  Field,
  Input,
  PageHeader,
  Select,
  Spinner,
  Table,
  Textarea,
} from "@/components/ui";

/**
 * Inspections, from the office.
 *
 * **The record of an inspection has existed since the first migration and nothing could reach it.**
 * The agent walks the flat with a phone; the manager, who is the person actually accountable for
 * what the visit found, had nowhere to put one in the diary, nowhere to read one back, and no way
 * to turn "the tap is sheared off" into a job somebody is held to. This screen is the diary half.
 *
 * Two decisions are worth knowing before changing anything here:
 *
 * - **A scheduled inspection starts with a checklist, not a blank sheet.** Nobody standing in a
 *   hallway types out twenty lines, and an inspection of three lines cannot be compared with
 *   anything. The switch is on by default and `DEFAULT_CHECKLIST` in `@rentorng/core` is the list.
 * - **The filter lives in the address**, as it does on every other list here, so `?due=1` — which
 *   is what the count in the subtitle means — survives a refresh and can be sent to a colleague.
 */

/* ---------------------------------------------------------------------- icons */

/**
 * Icons, drawn here rather than imported from a package.
 *
 * The same construction as `components/icons.tsx` — a 24 grid, 1.75 stroke, round caps and joins,
 * `currentColor` — so they sit with the rest of the set. They live with the three screens that use
 * them, in the way `agency-mark.tsx` keeps its own two.
 */
type IconProps = SVGProps<SVGSVGElement> & { size?: number };

function Icon({ size = 18, children, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      {children}
    </svg>
  );
}


/** A camera, for the photographs an inspector took. */
export const IconCamera = (props: IconProps) => (
  <Icon {...props}>
    <path d="M4 8.5h2.6l1.3-2h8.2l1.3 2H20a1 1 0 0 1 1 1v8.2a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5a1 1 0 0 1 1-1Z" />
    <circle cx="12" cy="13.4" r="3.1" />
  </Icon>
);

/** Down and to the right: this got worse since the last visit. */
export const IconWorse = (props: IconProps) => (
  <Icon {...props}>
    <path d="M4 7.5 12 15l3.2-3.1L20 16.5" />
    <path d="M20 12.2v4.3h-4.3" />
  </Icon>
);

/** Up and to the right: this was put right. */
export const IconBetter = (props: IconProps) => (
  <Icon {...props}>
    <path d="M4 16.5 12 9l3.2 3.1L20 7.5" />
    <path d="M20 11.8V7.5h-4.3" />
  </Icon>
);

/** A printer, on the one control that produces paper. */
export const IconPrinter = (props: IconProps) => (
  <Icon {...props}>
    <path d="M7 9V4.5h10V9" />
    <path d="M7 18H5.5A1.5 1.5 0 0 1 4 16.5v-5A1.5 1.5 0 0 1 5.5 10h13a1.5 1.5 0 0 1 1.5 1.5v5a1.5 1.5 0 0 1-1.5 1.5H17" />
    <rect x="7" y="14.5" width="10" height="5" rx="1" />
  </Icon>
);

/* ----------------------------------------------------------------------- list */

interface Row {
  id: string;
  reference: string | null;
  kind: string;
  status: string;
  scheduledFor: string | null;
  completedAt: string | null;
  unitLabel: string | null;
  propertyName: string | null;
  inspectorName: string | null;
  findings: number;
  lines: number;
  due: "overdue" | "due_today" | "upcoming" | "undated" | "done" | "cancelled";
}

interface ListResponse {
  inspections: Row[];
  page: PageMeta;
  staff: Array<{ id: string; name: string }>;
  today: string;
}

const STATUS_TONE: Record<string, "info" | "warn" | "success" | "muted"> = Object.fromEntries(
  INSPECTION_STATUSES.map((status) => [status.value, status.tone]),
) as Record<string, "info" | "warn" | "success" | "muted">;

/** The one figure a manager acts on: is this visit late? Everything else is a status. */
function DueBadge({ row }: { row: Row }) {
  if (row.due === "overdue") return <Badge tone="danger" dot>Overdue</Badge>;
  if (row.due === "due_today") return <Badge tone="warn" dot>Due today</Badge>;
  return <Badge tone={STATUS_TONE[row.status] ?? "muted"}>{INSPECTION_STATUSES.find((s) => s.value === row.status)?.label ?? row.status}</Badge>;
}

export function InspectionsPage() {
  const { vocab, can } = useSession();
  const fmt = useFormat();

  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? "";
  const due = params.get("due") === "1";
  const setFilter = (next: { status?: string; due?: boolean }) =>
    setParams(
      (current) => {
        const query = new URLSearchParams(current);
        if (next.status) query.set("status", next.status);
        else query.delete("status");
        if (next.due) query.set("due", "1");
        else query.delete("due");
        query.delete("page"); // a new filter starts at the beginning
        return query;
      },
      { replace: true },
    );

  const [adding, setAdding] = useState(false);
  /* Which of the two a new inspection is about. A unit is the common case by a long way; the
     common parts of a block — the compound, the gate, the tank — belong to a property and to no
     tenancy, and refusing to record them would be refusing half the job. */
  const [against, setAgainst] = useState<"unit" | "property">("unit");
  const [unitId, setUnitId] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [inspectorUserId, setInspectorUserId] = useState("");

  const list = useListParams();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["inspections", status, due, list.q, list.page],
    queryFn: () =>
      api.get<ListResponse>("/inspections", {
        status,
        due: due ? "1" : "",
        q: list.q,
        page: list.page,
      }),
    placeholderData: (previous) => previous,
  });

  const create = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.post<{ id: string }>("/inspections", input),
    invalidates: [["inspections"]],
    onSuccess: () => {
      setAdding(false);
      setUnitId("");
      setPropertyId("");
    },
  });

  const rows = data?.inspections ?? [];
  const overdue = rows.filter((row) => row.due === "overdue").length;
  const openFindings = rows.reduce((total, row) => total + row.findings, 0);

  return (
    <div>
      <PageHeader
        title="Inspections"
        subtitle={`${rows.length} shown${overdue ? ` · ${overdue} overdue` : ""}${
          openFindings ? ` · ${openFindings} findings` : ""
        }`}
        description="Condition visits, planned and completed. Every inspection is compared with the previous one of the same place, and anything found can be turned into a maintenance job without retyping it."
        actions={
          <>
            <ExportButton kind="inspections" />
            {can("maintenance.edit") ? (
              <Button onClick={() => setAdding((open) => !open)}>{adding ? "Close" : "Schedule an inspection"}</Button>
            ) : null}
          </>
        }
      />

      {adding ? (
        <Card className="mb-5">
          <CardTitle
            hint={
              <>
                The four kinds are not interchangeable on a report. A <strong>check-in</strong> records
                the condition a tenancy starts from, and everything later is measured against it; a{" "}
                <strong>check-out</strong> is the one a deposit deduction rests on. For example, a
                worktop graded “fair” at check-in and “damaged” at check-out is a deterioration the
                report will state in those words.
              </>
            }
          >
            New inspection
          </CardTitle>

          {create.errorMessage ? <Alert tone="danger">{create.errorMessage}</Alert> : null}

          <form
            className="grid gap-4 sm:grid-cols-3"
            onSubmit={(event) => {
              event.preventDefault();
              const form = new FormData(event.currentTarget);
              create.mutate({
                unitId: against === "unit" ? unitId || null : null,
                propertyId: against === "property" ? propertyId || null : null,
                kind: String(form.get("kind")),
                scheduledFor: String(form.get("scheduledFor")),
                inspectorUserId: inspectorUserId || null,
                summary: String(form.get("summary") || "") || null,
                useChecklist: form.get("useChecklist") === "on",
              });
            }}
          >
            <Field label="What is being inspected" htmlFor="against" group>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={against === "unit" ? "primary" : "secondary"}
                  onClick={() => setAgainst("unit")}
                >
                  {titleCase(vocab.unit)}
                </Button>
                <Button
                  size="sm"
                  variant={against === "property" ? "primary" : "secondary"}
                  onClick={() => setAgainst("property")}
                >
                  Common parts
                </Button>
              </div>
            </Field>

            {against === "unit" ? (
              <Field label={titleCase(vocab.unit)} htmlFor="unitId" required>
                <SearchSelect
                  id="unitId"
                  name="unitId"
                  value={unitId}
                  onChange={setUnitId}
                  search={searchUnits}
                  resolve={resolveUnit}
                  placeholder={`Choose a ${vocab.unit}`}
                  emptyMessage={`No ${vocab.unit} matched.`}
                />
              </Field>
            ) : (
              <Field
                label={titleCase(vocab.property)}
                htmlFor="propertyId"
                required
                hint="For the parts nobody rents — the compound, the gate, the water storage."
              >
                <SearchSelect
                  id="propertyId"
                  name="propertyId"
                  value={propertyId}
                  onChange={setPropertyId}
                  search={searchProperties}
                  resolve={resolveProperty}
                  placeholder={`Choose a ${vocab.property}`}
                  emptyMessage={`No ${vocab.property} matched.`}
                />
              </Field>
            )}

            <Field label="Kind" htmlFor="kind">
              <Select id="kind" name="kind" defaultValue="routine">
                {INSPECTION_KINDS.map((kind) => (
                  <option key={kind.value} value={kind.value}>
                    {kind.label}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label="Date" htmlFor="scheduledFor" required>
              <Input id="scheduledFor" name="scheduledFor" type="date" required defaultValue={data?.today ?? ""} />
            </Field>

            <Field label="Inspector" htmlFor="inspectorUserId" hint="Who is going. It can be changed later.">
              <SearchSelect
                id="inspectorUserId"
                name="inspectorUserId"
                value={inspectorUserId}
                onChange={setInspectorUserId}
                search={searchStaff}
                resolve={resolveStaff}
                placeholder="Not assigned"
                emptyMessage="Nobody matched."
              />
            </Field>

            <label className="flex items-start gap-2 self-end text-sm">
              <input
                type="checkbox"
                name="useChecklist"
                defaultChecked
                className="mt-0.5 size-4 rounded border-ink-300"
                aria-describedby="checklist-hint"
              />
              <span>
                Start from the standard checklist
                <span id="checklist-hint" className="block text-xs text-ink-500">
                  Nineteen lines, room by room, each ungraded until somebody grades it.
                </span>
              </span>
            </label>

            <Field label="Note for the inspector" htmlFor="summary">
              <Textarea id="summary" name="summary" rows={2} placeholder="Anything they should look at in particular." />
            </Field>

            <div className="sm:col-span-3">
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? "Scheduling…" : "Schedule it"}
              </Button>
            </div>
          </form>
        </Card>
      ) : null}

      <div className="mb-4 flex flex-wrap gap-2">
        <Button variant={!status && !due ? "primary" : "secondary"} size="sm" onClick={() => setFilter({})}>
          All
        </Button>
        {/* "Due" is one filter across two statuses, and it is what the subtitle counts. */}
        <Button variant={due ? "primary" : "secondary"} size="sm" onClick={() => setFilter({ due: true })}>
          Due
        </Button>
        {INSPECTION_STATUSES.map((option) => (
          <Button
            key={option.value}
            variant={status === option.value ? "primary" : "secondary"}
            size="sm"
            onClick={() => setFilter({ status: option.value })}
          >
            {option.label}
          </Button>
        ))}
      </div>

      <Card padded={false}>
        <div className="border-b border-ink-200 p-3">
          <SearchBox params={list} placeholder={`Search a reference, ${vocab.property} or ${vocab.unit}…`} />
        </div>

        {isLoading ? (
          <Spinner />
        ) : error ? (
          <ErrorState error={error} retry={() => refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No inspections here."
            hint="Schedule one against a unit or against the common parts of a property. The first one becomes the record everything later is compared with."
          />
        ) : (
          <Table
            head={
              <tr>
                <th>Reference</th>
                <th>Date</th>
                <th>Where</th>
                <th>Kind</th>
                <th>Inspector</th>
                <th className="num">Findings</th>
                <th>Status</th>
              </tr>
            }
          >
            {rows.map((row) => (
              <tr key={row.id}>
                <td>
                  <Link
                    to={`/app/inspections/${row.id}`}
                    className="inline-flex items-center gap-2 font-medium text-brand-700 hover:underline"
                  >
                    <IconClipboardCheck size={16} />
                    {row.reference ?? "—"}
                  </Link>
                </td>
                <td className="text-ink-600">{fmt.date(row.scheduledFor)}</td>
                <td className="text-ink-500">
                  {row.unitLabel ?? "Common parts"}
                  {row.propertyName ? <span className="block text-xs text-ink-400">{row.propertyName}</span> : null}
                </td>
                <td className="text-ink-600">{inspectionKindLabel(row.kind)}</td>
                <td className="text-ink-500">{row.inspectorName ?? "Not assigned"}</td>
                <td className="num tabular">
                  {row.findings > 0 ? (
                    <span className="font-semibold text-danger-600">{row.findings}</span>
                  ) : (
                    <span className="text-ink-400">0</span>
                  )}
                  <span className="text-xs text-ink-400"> / {row.lines}</span>
                </td>
                <td>
                  <DueBadge row={row} />
                </td>
              </tr>
            ))}
          </Table>
        )}

        <Pagination meta={data?.page} params={list} noun="inspections" />
      </Card>
    </div>
  );
}
